import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { message } from 'antd'
import moment from 'moment'
import { getT3cSdkUrl, getCallDuration } from '@services/t3c'
import { getHotline400Config } from '@services/lead/hotline'
import { injectTcccWebSDK } from 'src/library/t3c'
import { sessionStore } from './session-store'
import { moduleId } from '@configs'
import { ASRItem, CallInfo, CallStatusString } from 'src/types/ai-assistant'
import { registerT3cBaseListeners } from './t3c-listeners'
import { UserInfo } from '@types'
import { LogType } from '@tencent/aegis-web-sdk-v2'

// T3C 初始化阶段枚举
export type T3CInitStage =
  | 'idle' // 未开始
  | 'fetching_url' // 正在获取 SDK URL
  | 'fetch_url_failed' // 获取 SDK URL 失败
  | 'loading_script' // 正在加载 SDK 脚本
  | 'script_failed' // SDK 脚本加载失败
  | 'script_loaded' // SDK 脚本已加载，等待 ready 事件
  | 'ready' // SDK 完全就绪

// T3C Store 状态接口
interface T3CState {
  // ===== T3C SDK 状态 =====
  t3cReady: boolean
  t3cfailed: boolean
  t3cErrorMsg: string
  t3cSdkReady: boolean
  t3cSdkReadyTime: string
  t3cSdkUrl: string
  curTabUUID: string
  /**
   * 当前这通电话的归属信息：哪个标签页发起/接入了它。
   */
  callOwner: { sessionId: string; tabUUID: string } | null
  t3cInitStage: T3CInitStage

  // ===== 通话状态 =====
  callStatus: CallStatusString
  sessionId: string
  isCallMute: boolean
  isCallHold: boolean
  showCallWorkbench: boolean
  // T3C 工作台（SDK 内置 UI）是否可见：SDK 默认显示，初始化时按权限隐藏则置 false
  isWorkbenchVisible: boolean
  // 当前是否停留在 /ai-calling（AI 实时辅助）页：该页自带通话控制并隐藏 T3C 外呼浮层，
  // 故 callOuted 等事件在本页时不应强制 UI.show() 把浮层显示出来。由页面挂载/卸载维护。
  isAiCallingActive: boolean
  callInfo: CallInfo
  asrList: ASRItem[]
  /**
   * 实时 ASR 提交完成的时间戳信号，仅用于通知页面重查小结状态。
   * SubmitRealtimeAsr 才是后端生成小结的触发点，且其返回晚于 callStatus 置 idle，
   * 故需单独通知；用时间戳可支持连续多通电话，resetCallState 不清除它。
   */
  realtimeAsrSubmittedAt: number

  // ===== 配置 =====
  hotline400Config: { list: any[] }
  callDuration: { callDuration: string; callTime: number }

  // ===== 状态更新方法 =====
  setT3cReady: (ready: boolean) => void
  setT3cFailed: (failed: boolean) => void
  setT3cErrorMsg: (msg: string) => void
  setCurTabUUID: (uuid: string) => void
  /** 记录通话归属（sessionId + 发起该通话的 tabUUID）。tabUUID 传空表示 SDK 未下发 */
  setCallOwner: (sessionId: string, tabUUID: string) => void
  setT3cSdkReady: (ready: boolean) => void
  setT3cSdkReadyTime: (time: string) => void
  setT3cSdkUrl: (url: string) => void
  setT3cInitStage: (stage: T3CInitStage) => void
  setCallStatus: (status: CallStatusString) => void
  setSessionId: (id: string) => void
  setCallInfo: (
    data: string | CallInfo | ((draft: CallInfo) => void),
    value?: any
  ) => void
  setShowCallWorkbench: (show: boolean) => void
  setIsCallMute: (mute: boolean) => void
  setIsCallHold: (hold: boolean) => void
  setIsWorkbenchVisible: (visible: boolean) => void
  setIsAiCallingActive: (active: boolean) => void
  /** 标记实时 ASR 已提交完成（由 t3c-listeners 在 SubmitRealtimeAsr 返回后调用） */
  markRealtimeAsrSubmitted: () => void
  setAsrList: (list: ASRItem[]) => void
  setHotline400Config: (config: { list: any[] }) => void
  setCallDuration: (
    data: string | { callDuration: string; callTime: number } | ((draft: { callDuration: string; callTime: number }) => void),
    value?: any
  ) => void

  // ===== 业务方法 =====
  handleAsrEvent: (res: any) => void
  getTodayCallDuration: () => Promise<void>
  getHotline400ConfigOnce: () => Promise<void>
  initT3cSdk: (params?: { refresh?: boolean }, onSdkReady?: () => void, userInfo?: UserInfo) => Promise<void>
  reinitT3cSdk: (params?: { refresh?: boolean }) => Promise<void>
  resetCallState: () => void
}

// 初始状态
const initialCallInfo: CallInfo = {
  calleeLocation: '',
  calleePhoneNumberText: '',
  callerPhoneNumberText: '',
  calleeName: '',
  recordId: undefined,
}

// 创建 T3C Store
export const t3cStore = create<T3CState>()(
  immer((set) => ({
    // ===== 初始状态 =====
    t3cReady: false,
    t3cfailed: false,
    t3cErrorMsg: '',
    t3cSdkReady: false,
    t3cSdkReadyTime: '',
    t3cSdkUrl: '',
    curTabUUID: '',
    callOwner: null as T3CState['callOwner'],
    t3cInitStage: 'idle' as T3CInitStage,

    callStatus: 'idle',
    sessionId: '',
    isCallMute: false,
    isCallHold: false,
    showCallWorkbench: false,
    isWorkbenchVisible: true,
    isAiCallingActive: false,
    callInfo: initialCallInfo,
    asrList: [],
    realtimeAsrSubmittedAt: 0,

    hotline400Config: { list: [] },
    callDuration: { callDuration: '00:00', callTime: 0 },

    // ===== 状态更新方法 =====
    setT3cReady: (ready) => set(draft => { draft.t3cReady = ready }),
    setT3cFailed: (failed) => set(draft => { draft.t3cfailed = failed }),
    setT3cErrorMsg: (msg) => set(draft => { draft.t3cErrorMsg = msg }),
    setCurTabUUID: (uuid) => set(draft => { draft.curTabUUID = uuid }),
    setCallOwner: (sessionId, tabUUID) => set(draft => { draft.callOwner = { sessionId, tabUUID } }),
    setT3cSdkReady: (ready) => set(draft => { draft.t3cSdkReady = ready }),
    setT3cSdkReadyTime: (time) => set(draft => { draft.t3cSdkReadyTime = time }),
    setT3cSdkUrl: (url) => set(draft => { draft.t3cSdkUrl = url }),
    setT3cInitStage: (stage) => set(draft => { draft.t3cInitStage = stage }),
    setCallStatus: (status) => set(draft => { draft.callStatus = status }),
    setSessionId: (id) => set(draft => { draft.sessionId = id }),
    setCallInfo: (data: any, value?: any) => set(draft => {
      if (typeof data === 'string') {
        // 方式 1: 单个属性设置 setCallInfo('calleeName', 'John')
        ;(draft.callInfo as any)[data] = value
      } else if (typeof data === 'function') {
        // 方式 3: 函数式更新，兼容两种写法：
        //   a) mutate draft：  setCallInfo(draft => { draft.calleeName = 'John' })
        //   b) 返回新对象：     setCallInfo(info => ({ ...info, calleeName: 'John' }))
        // 迁移自 unstated-next 的调用点多用 (b)，若仅执行回调而不接收返回值会导致更新丢失
        const result = data(draft.callInfo)
        if (result && typeof result === 'object') {
          draft.callInfo = result
        }
      } else if (typeof data === 'object' && data !== null) {
        // 方式 2: 对象方式 setCallInfo({ calleeName: 'John', calleeLocation: 'Beijing' })
        Object.assign(draft.callInfo, data)
      }
    }),
    setShowCallWorkbench: (show) => set(draft => { draft.showCallWorkbench = show }),
    setIsCallMute: (mute) => set(draft => { draft.isCallMute = mute }),
    setIsCallHold: (hold) => set(draft => { draft.isCallHold = hold }),
    setIsWorkbenchVisible: (visible) => set(draft => { draft.isWorkbenchVisible = visible }),
    setIsAiCallingActive: (active) => set(draft => { draft.isAiCallingActive = active }),
    markRealtimeAsrSubmitted: () => set(draft => { draft.realtimeAsrSubmittedAt = Date.now() }),
    setAsrList: (list) => set(draft => { draft.asrList = list }),
    setHotline400Config: (config) => set(draft => { draft.hotline400Config = config }),
    setCallDuration: (data: any, value?: any) => set(draft => {
      if (typeof data === 'string') {
        // 方式 1: 单个属性设置 setCallDuration('callDuration', '10:30')
        ;(draft.callDuration as any)[data] = value
      } else if (typeof data === 'function') {
        // 方式 3: 函数式更新，兼容 mutate draft 与返回新对象两种写法
        const result = data(draft.callDuration)
        if (result && typeof result === 'object') {
          draft.callDuration = result
        }
      } else if (typeof data === 'object' && data !== null) {
        // 方式 2: 对象方式 setCallDuration({ callDuration: '10:30', callTime: 630 })
        Object.assign(draft.callDuration, data)
      }
    }),

    // ===== 业务方法 =====
    handleAsrEvent: (res) => {
      console.log('语音识别事件 window.tccc.events.asr.', res, res.result.slice_type)
      if (!res?.result?.voice_text_str?.trim()) {
        return
      }

      set(draft => {
        const currentList = draft.asrList
        const matchMessage = [...currentList].reverse().find(item => item.flow === res.flow)

        // 如果没有匹配到消息，则说明这是一条新进来的消息，直接添加到列表中
        if (!matchMessage) {
          draft.asrList = [...currentList, {
            flow: res.flow,
            message: res.result.voice_text_str,
            timestamp: res.result.asr_timestamp,
            sliceType: res.result.slice_type,
            index: res.result.index,
          }]
          return
        }

        // 如果匹配到了一条消息，并且sliceType等于2，说明这条消息结束了，需要给列表增加一条新消息
        if (matchMessage.sliceType === 2) {
          draft.asrList = [...currentList, {
            flow: res.flow,
            message: res.result.voice_text_str,
            timestamp: res.result.asr_timestamp,
            sliceType: res.result.slice_type,
            index: res.result.index,
          }]
          return
        }

        // 如果匹配到了一条消息，并且sliceType不等于2，说明这条消息还在继续，需要把这条消息的message更新为最新
        const modifyMessageIndex = currentList.findIndex(item => item.flow === matchMessage.flow && item.index === matchMessage.index)
        const newList = [...currentList]

        if (res.result.slice_type === 2) {
          newList.splice(modifyMessageIndex, 1)
          newList.push({
            message: res.result.voice_text_str,
            sliceType: res.result.slice_type,
            index: res.result.index,
            flow: res.flow,
            timestamp: res.result.asr_timestamp,
          })
        } else {
          newList[modifyMessageIndex] = {
            message: res.result.voice_text_str,
            sliceType: res.result.slice_type,
            index: res.result.index,
            flow: res.flow,
            timestamp: res.result.asr_timestamp,
          }
        }
        draft.asrList = newList
      })
    },

    getTodayCallDuration: async () => {
      const [res, err] = await getCallDuration()
      if (err) {
        message.error(err.message)
        return
      }
      const duration = moment.utc(res?.callDuration * 1000).format(res?.callDuration < 3600 ? 'mm:ss' : 'HH:mm:ss')
      set(draft => {
        draft.callDuration = {
          callDuration: duration,
          callTime: res.callTime,
        }
      })
    },

    getHotline400ConfigOnce: async () => {
      const [config] = await getHotline400Config()
      set(draft => {
        draft.hotline400Config = config
      })
    },

    initT3cSdk: async (params, onSdkReady, userInfo) => {
      set(draft => {
        draft.t3cReady = false
        draft.t3cInitStage = 'fetching_url'
      })

      const [sdkUrl, err] = await getT3cSdkUrl(params ? { refresh: params.refresh } : undefined)
      if (sdkUrl && sdkUrl.sdkUrl) {
        set(draft => { draft.t3cInitStage = 'loading_script' })
        injectTcccWebSDK(sdkUrl.sdkUrl, () => {
          set(draft => {
            draft.t3cSdkUrl = sdkUrl.sdkUrl
            draft.t3cSdkReadyTime = new Date().toLocaleString()
            draft.t3cSdkReady = true
            draft.t3cInitStage = 'script_loaded'
          })
          window.AegisV2?.reportEvent({
            name: 'tccc_sdk_load_success',
            type: 'custom_event' as LogType,
            msg: 'tccc sdk 加载成功',
            eventId: Date.now(),
            user: userInfo?.salesName,
          })
          // 加载JS SDK文件成功，此时可使用全局变量"tccc"
          window.tccc.UI.hidefloatButton()

          // 根据用户权限决定是否隐藏工作台（同步 store，供工作台开关按钮读取初始状态）
          const { isPermissableBelongModule } = sessionStore.getState()
          if (!isPermissableBelongModule([moduleId.MID_LONG_TAIL_CLEAN_ID])) {
            window.tccc.UI.hideWorkbench()
            set(draft => { draft.isWorkbenchVisible = false })
          }

          // 调用回调，让组件注册监听器
          onSdkReady?.()
        }, userInfo, () => {
          // script 加载失败回调
          set(draft => {
            draft.t3cInitStage = 'script_failed'
            draft.t3cErrorMsg = `SDK脚本加载失败: ${sdkUrl.sdkUrl}`
          })
        })
      } else {
        const errorMsg = err?.message || (sdkUrl ? `CreateLoginToken返回数据异常: sdkUrl为空(${JSON.stringify(sdkUrl)})` : '获取SDK地址失败')
        set(draft => {
          draft.t3cInitStage = 'fetch_url_failed'
          draft.t3cErrorMsg = errorMsg
        })
        message.error(`T3C初始化失败: ${errorMsg}`)
      }
    },

    resetCallState: () => {
      set(draft => {
        draft.asrList = []
        draft.sessionId = ''
        draft.isCallMute = false
        draft.isCallHold = false
        draft.callStatus = 'idle'
        draft.showCallWorkbench = false
        draft.callInfo = {
          calleeLocation: '',
          calleePhoneNumberText: '',
          callerPhoneNumberText: '',
          calleeName: '',
          recordId: undefined,
        }
      })
    },

    reinitT3cSdk: async (params) => {
      // 重新初始化 T3C SDK，使用统一的监听器注册函数
      const { initT3cSdk } = t3cStore.getState()

      // 使用统一的基础监听器注册函数
      const onSdkReady = () => {
        registerT3cBaseListeners()
      }

      // 调用初始化
      await initT3cSdk(params, onSdkReady)
    },
  }))
)
